/**
 * Newsletter Popup - SalonID
 * Vanilla JavaScript for Shopify integration
 */

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', class NewsletterPopup extends HTMLElement {
    constructor() {
      super();

      // Configuration from data attributes
      this.key = this.dataset.storageKey;
      this.testMode = this.dataset.testMode === 'true';
      this.delay = parseInt(this.dataset.delay) || 2000;
      this.mobileDelay = parseInt(this.dataset.mobileDelay) || 30000;
      this.frequency = this.dataset.frequency || 'session';
      this.counterMin = parseInt(this.dataset.counterMin) || 35;
      this.counterMax = parseInt(this.dataset.counterMax) || 55;

      // DOM Elements
      this.backdrop = this.querySelector('[data-backdrop]');
      this.modal = this.querySelector('[data-modal]');
      this.content = this.querySelector('[data-content]');
      this.success = this.querySelector('[data-success]');
      this.closeBtns = this.querySelectorAll('[data-close]');
      this.declineBtn = this.querySelector('[data-decline]');
      this.form = this.querySelector('form.newsletter-popup__form');
      this.emailInput = this.querySelector('[data-email-input]');
      this.inputWrapper = this.querySelector('[data-input-wrapper]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.counterValue = this.querySelector('[data-counter-value]');

      // State
      this.isOpen = false;
      this.hasShown = false;
      this.counterInterval = null;
      this.currentCount = this.getRandomCount();

      // Bind methods
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleExitIntent = this.handleExitIntent.bind(this);

      // Initialize
      this.init();
    }

    init() {
      console.log('[Newsletter Popup] Initializing...');

      // Set initial counter value
      if (this.counterValue) {
        this.counterValue.textContent = this.currentCount;
      }

      // Set up event listeners
      this.setupEventListeners();

      // Expose global trigger
      window.showNewsletterPopup = () => this.open();

      // Check for form submission (flag set by inline script in Liquid)
      if (this.checkFormSubmission()) {
        console.log('[Newsletter Popup] Form submission detected');
        return;
      }

      // Test mode - show after delay
      if (this.testMode) {
        setTimeout(() => this.open(), this.delay);
        return;
      }

      // Check if we should show the popup
      if (this.shouldShow()) {
        this.setupTriggers();
      }
    }

    /**
     * Check if user just submitted the form
     */
    checkFormSubmission() {
      console.log('[Newsletter Popup] Checking form submission, flag:', window.__newsletterFormSubmitted);

      if (window.__newsletterFormSubmitted) {
        // Clean up global flag
        delete window.__newsletterFormSubmitted;

        // Prevent showing again in same session
        if (!sessionStorage.getItem(this.key + '_submitted')) {
          sessionStorage.setItem(this.key + '_submitted', 'true');
          this.showSuccessOnly();
          return true;
        }
      }

      return false;
    }

    /**
     * Show only the success state (after form redirect)
     */
      showSuccessOnly() {
        console.log('[Newsletter Popup] Showing success state');

        this.isOpen = true;
        this.hasShown = true;

        // Lock body scroll FIRST
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = '0';
        document.body.style.left = '0';
        document.body.style.right = '0';

        // Hide content, show success using classes (not hidden attribute)
        if (this.content) this.content.classList.add('is-hidden');
        if (this.success) this.success.classList.add('is-visible');

        // Show popup
        this.classList.add('is-open');

        // Track conversion
        this.trackEvent('Lead', {
          content_name: 'Newsletter Popup',
          lead_type: 'newsletter'
        });

        // Auto close after 5 seconds
        setTimeout(() => {
          this.close();
        }, 5000);
      }

    setupEventListeners() {
      // Close buttons
      this.closeBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      // Decline button
      this.declineBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });

      // Backdrop click
      this.backdrop?.addEventListener('click', () => this.close());

      // Form submission
      this.form?.addEventListener('submit', this.handleSubmit);

      // Input focus states
      this.emailInput?.addEventListener('focus', () => {
        this.inputWrapper?.classList.add('is-focused');
      });

      this.emailInput?.addEventListener('blur', () => {
        this.inputWrapper?.classList.remove('is-focused');
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }

    setupTriggers() {
      const isMobile = window.innerWidth < 768;

      if (isMobile) {
        setTimeout(() => {
          if (!this.hasShown) {
            this.open();
          }
        }, this.mobileDelay);
      } else {
        document.addEventListener('mouseleave', this.handleExitIntent);

        setTimeout(() => {
          if (!this.hasShown) {
            this.open();
          }
        }, this.delay);
      }
    }

    handleExitIntent(e) {
      if (e.clientY <= 0 && !this.hasShown) {
        this.open();
        document.removeEventListener('mouseleave', this.handleExitIntent);
      }
    }

    shouldShow() {
      if (this.frequency === 'session' || this.frequency === 'once') {
        return !sessionStorage.getItem(this.key);
      }

      const lastShown = localStorage.getItem(this.key);
      if (!lastShown) return true;

      const daysSinceShown = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60 * 24);
      const frequencyDays = this.getFrequencyDays();

      return daysSinceShown >= frequencyDays;
    }

    getFrequencyDays() {
      const map = {
        '1_day': 1,
        '7_days': 7,
        '30_days': 30
      };
      return map[this.frequency] || 7;
    }

    markAsShown() {
      if (this.frequency === 'session' || this.frequency === 'once') {
        sessionStorage.setItem(this.key, Date.now().toString());
      } else {
        localStorage.setItem(this.key, Date.now().toString());
      }
    }

    getRandomCount() {
      return Math.floor(Math.random() * (this.counterMax - this.counterMin + 1)) + this.counterMin;
    }

    open() {
      if (this.isOpen || this.hasShown) return;

      if (document.body.classList.contains('overflow-hidden')) {
        setTimeout(() => this.open(), 3000);
        return;
      }

      this.isOpen = true;
      this.hasShown = true;
      this.markAsShown();

      // Store scroll position for iOS
      this.scrollPosition = window.pageYOffset;

      this.classList.add('is-open');
      
      // Lock body scroll (iOS compatible)
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';

      // Focus email input after animation (skip on mobile to prevent keyboard jump)
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        setTimeout(() => {
          this.emailInput?.focus();
        }, 400);
      }

      this.startCounterAnimation();
      this.trackEvent('NewsletterPopupShown');
    }

    close() {
      // Blur active element to prevent scroll issues
      if (document.activeElement) {
        document.activeElement.blur();
      }

      this.isOpen = false;
      this.classList.remove('is-open');
      
      // Restore body scroll (iOS compatible)
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      
      // Restore scroll position
      if (this.scrollPosition !== undefined) {
        window.scrollTo(0, this.scrollPosition);
      }

      if (this.counterInterval) {
        clearInterval(this.counterInterval);
        this.counterInterval = null;
      }
    }

    startCounterAnimation() {
      const maxCount = this.currentCount + 5;

      this.counterInterval = setInterval(() => {
        if (Math.random() > 0.7 && this.currentCount < maxCount) {
          this.currentCount++;
          if (this.counterValue) {
            this.counterValue.textContent = this.currentCount;
          }
        }
      }, 8000);
    }

    handleSubmit(e) {
      const email = this.emailInput?.value;

      if (!email || !this.isValidEmail(email)) {
        e.preventDefault();
        this.showInputError();
        return;
      }

      this.submitBtn?.classList.add('is-loading');
      this.submitBtn?.setAttribute('disabled', 'true');
      this.trackEvent('NewsletterSubmitAttempt');
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    showInputError() {
      this.inputWrapper?.classList.add('has-error');
      this.emailInput?.focus();

      setTimeout(() => {
        this.inputWrapper?.classList.remove('has-error');
      }, 3000);

      this.emailInput?.addEventListener('input', () => {
        this.inputWrapper?.classList.remove('has-error');
      }, { once: true });
    }

    trackEvent(eventName, params = {}) {
      if (typeof fbq !== 'undefined') {
        if (eventName === 'Lead') {
          fbq('track', 'Lead', params);
        } else {
          fbq('trackCustom', eventName, params);
        }
      }

      if (typeof gtag !== 'undefined') {
        gtag('event', eventName.toLowerCase(), params);
      }

      if (typeof Shopify !== 'undefined' && Shopify.analytics) {
        Shopify.analytics.publish('newsletter_popup', {
          event: eventName,
          ...params
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('mouseleave', this.handleExitIntent);
      if (this.counterInterval) {
        clearInterval(this.counterInterval);
      }
      delete window.showNewsletterPopup;
    }
  });
}